import type { CaseInfrastructureTabModalsProps } from './CaseInfrastructureTabModals'
import type { CaseInfrastructureTabState } from './use-case-infrastructure-tab'

type Props = CaseInfrastructureTabModalsProps

function mapDataProps(
  state: CaseInfrastructureTabState
): Pick<
  Props,
  | 'caseId'
  | 'endpoints'
  | 'relationships'
  | 'questionTemplates'
  | 'selectedIds'
  | 'selectionCount'
  | 'existingCustomRoles'
  | 'getRelationshipCount'
  | 'refetch'
> {
  const {
    caseId,
    endpoints,
    relationships,
    questionTemplates,
    existingCustomRoles,
    sel,
    getRelationshipCount,
    refetch,
  } = state
  return {
    caseId,
    endpoints,
    relationships,
    questionTemplates,
    selectedIds: sel.selectedIds,
    selectionCount: sel.count,
    existingCustomRoles,
    getRelationshipCount,
    refetch,
  }
}

function mapStatusProps(
  state: CaseInfrastructureTabState
): Pick<
  Props,
  | 'creating'
  | 'deleting'
  | 'importing'
  | 'bulkDeleting'
  | 'creatingRelationship'
  | 'deletingRelationship'
  | 'movingRelationship'
  | 'copyingLinks'
  | 'copyError'
  | 'mutationError'
  | 'relationshipError'
  | 'clearError'
  | 'clearRelationshipError'
> {
  const { endpointMut, relMut, modals } = state
  return {
    creating: endpointMut.creating,
    deleting: endpointMut.deleting,
    importing: endpointMut.importing,
    bulkDeleting: modals.bulkDeleting,
    creatingRelationship: relMut.creating,
    deletingRelationship: relMut.deleting,
    movingRelationship: modals.movingRelationship,
    copyingLinks: modals.copyingLinks,
    copyError: modals.copyError,
    mutationError: endpointMut.error,
    relationshipError: relMut.error,
    clearError: endpointMut.clearError,
    clearRelationshipError: relMut.clearError,
  }
}

function mapModalProps(
  state: CaseInfrastructureTabState
): Pick<
  Props,
  | 'showCreateModal'
  | 'setShowCreateModal'
  | 'editingEndpoint'
  | 'setEditingEndpoint'
  | 'linkingEndpoint'
  | 'setLinkingEndpoint'
  | 'showCsvImportModal'
  | 'setShowCsvImportModal'
  | 'showJsonImportModal'
  | 'setShowJsonImportModal'
  | 'showNewImportModal'
  | 'setShowNewImportModal'
  | 'showRelationshipImportModal'
  | 'setShowRelationshipImportModal'
  | 'viewLinksEndpoint'
  | 'setViewLinksEndpoint'
  | 'editingRelationship'
  | 'setEditingRelationship'
  | 'bulkLinkSourceEndpoint'
  | 'setBulkLinkSourceEndpoint'
  | 'copyLinksTargetEndpoint'
  | 'setCopyLinksTargetEndpoint'
  | 'setCopyError'
  | 'showViewMapSetModal'
  | 'setShowViewMapSetModal'
  | 'showViewCollectionSetModal'
  | 'setShowViewCollectionSetModal'
  | 'deleteConfirmation'
  | 'setDeleteConfirmation'
> {
  const { modals } = state
  return {
    showCreateModal: modals.showCreateModal,
    setShowCreateModal: modals.setShowCreateModal,
    editingEndpoint: modals.editingEndpoint,
    setEditingEndpoint: modals.setEditingEndpoint,
    linkingEndpoint: modals.linkingEndpoint,
    setLinkingEndpoint: modals.setLinkingEndpoint,
    showCsvImportModal: modals.showCsvImportModal,
    setShowCsvImportModal: modals.setShowCsvImportModal,
    showJsonImportModal: modals.showJsonImportModal,
    setShowJsonImportModal: modals.setShowJsonImportModal,
    showNewImportModal: modals.showNewImportModal,
    setShowNewImportModal: modals.setShowNewImportModal,
    showRelationshipImportModal: modals.showRelationshipImportModal,
    setShowRelationshipImportModal: modals.setShowRelationshipImportModal,
    viewLinksEndpoint: modals.viewLinksEndpoint,
    setViewLinksEndpoint: modals.setViewLinksEndpoint,
    editingRelationship: modals.editingRelationship,
    setEditingRelationship: modals.setEditingRelationship,
    bulkLinkSourceEndpoint: modals.bulkLinkSourceEndpoint,
    setBulkLinkSourceEndpoint: modals.setBulkLinkSourceEndpoint,
    copyLinksTargetEndpoint: modals.copyLinksTargetEndpoint,
    setCopyLinksTargetEndpoint: modals.setCopyLinksTargetEndpoint,
    setCopyError: modals.setCopyError,
    showViewMapSetModal: modals.showViewMapSetModal,
    setShowViewMapSetModal: modals.setShowViewMapSetModal,
    showViewCollectionSetModal: modals.showViewCollectionSetModal,
    setShowViewCollectionSetModal: modals.setShowViewCollectionSetModal,
    deleteConfirmation: modals.deleteConfirmation,
    setDeleteConfirmation: modals.setDeleteConfirmation,
  }
}

function mapContextMenuProps(
  state: CaseInfrastructureTabState
): Pick<
  Props,
  | 'contextMenu'
  | 'setContextMenu'
  | 'mapContextMenu'
  | 'setMapContextMenu'
  | 'collectionContextMenu'
  | 'setCollectionContextMenu'
  | 'deleteContextMenu'
  | 'setDeleteContextMenu'
> {
  const { modals } = state
  return {
    contextMenu: modals.contextMenu,
    setContextMenu: modals.setContextMenu,
    mapContextMenu: modals.mapContextMenu,
    setMapContextMenu: modals.setMapContextMenu,
    collectionContextMenu: modals.collectionContextMenu,
    setCollectionContextMenu: modals.setCollectionContextMenu,
    deleteContextMenu: modals.deleteContextMenu,
    setDeleteContextMenu: modals.setDeleteContextMenu,
  }
}

function mapSetProps(
  state: CaseInfrastructureTabState
): Pick<
  Props,
  | 'mapSetIds'
  | 'mapSetCount'
  | 'isInSet'
  | 'toggleMapSet'
  | 'removeManyFromMapSet'
  | 'clearMapSet'
  | 'collectionSetIds'
  | 'collectionSetCount'
  | 'isInCollectionSet'
  | 'toggleCollectionSet'
  | 'removeFromCollectionSet'
  | 'clearCollectionSet'
> {
  const { mapSet, collectionSet, actions } = state
  return {
    mapSetIds: mapSet.endpointIds,
    mapSetCount: mapSet.count,
    isInSet: mapSet.isInSet,
    toggleMapSet: mapSet.toggle,
    removeManyFromMapSet: mapSet.removeMany,
    clearMapSet: mapSet.clearAll,
    collectionSetIds: collectionSet.taggedEndpointIds,
    collectionSetCount: collectionSet.count,
    isInCollectionSet: collectionSet.isInSet,
    toggleCollectionSet: actions.toggleCollectionSet,
    removeFromCollectionSet: collectionSet.removeFromSet,
    clearCollectionSet: collectionSet.clearSet,
  }
}

function mapHandlerProps(
  state: CaseInfrastructureTabState
): Pick<
  Props,
  | 'handleAddSelectedToMapSet'
  | 'handleAddSelectedToCollectionSet'
  | 'handleCreate'
  | 'handleUpdate'
  | 'handleCreateRelationship'
  | 'handleImport'
  | 'handleUpdateRelationship'
  | 'handleDeleteRelationship'
  | 'handleBulkCreateRelationships'
  | 'handleCopyLinks'
  | 'handleMoveRelationship'
  | 'handleDeleteSingle'
  | 'handleDeleteSelected'
  | 'handleBulkDelete'
> {
  const { actions, handlers } = state
  return {
    handleAddSelectedToMapSet: actions.handleAddSelectedToMapSet,
    handleAddSelectedToCollectionSet: actions.handleAddSelectedToCollectionSet,
    handleCreate: handlers.handleCreate,
    handleUpdate: handlers.handleUpdate,
    handleCreateRelationship: handlers.handleCreateRelationship,
    handleImport: handlers.handleImport,
    handleUpdateRelationship: handlers.handleUpdateRelationship,
    handleDeleteRelationship: handlers.handleDeleteRelationship,
    handleBulkCreateRelationships: handlers.handleBulkCreateRelationships,
    handleCopyLinks: handlers.handleCopyLinks,
    handleMoveRelationship: handlers.handleMoveRelationship,
    handleDeleteSingle: actions.handleDeleteSingle,
    handleDeleteSelected: actions.handleDeleteSelected,
    handleBulkDelete: actions.handleBulkDelete,
  }
}

export function mapStateToModalProps(state: CaseInfrastructureTabState): Props {
  return {
    ...mapDataProps(state),
    ...mapStatusProps(state),
    ...mapModalProps(state),
    ...mapContextMenuProps(state),
    ...mapSetProps(state),
    ...mapHandlerProps(state),
  }
}
